/**
 * Stripe event payload schemas. PRD-003 TECH-12, DATA-13, PR-03, PR-04, PR-51.
 *
 * **Note on api-schemas additions:** The package was treated as frozen after
 * T-005. This module is added in Phase E (T-027 - T-031) because the Stripe
 * webhook handler (T-029) needs typed event payloads to refuse anything that
 * does not match the expected shape. Surfacing this expansion in IMPL-NOTES so
 * the learner can decide whether to formalize the addition or fold it into a
 * later schema-version bump.
 *
 * The schemas here are intentionally minimal: only the fields the webhook
 * handler reads. Stripe events carry hundreds of fields; pulling them all into
 * a Zod schema would be brittle and would break on any Stripe API minor
 * version. Instead, we validate the leaf fields we actually use and pass the
 * rest through as `passthrough()` on the parent object.
 */

import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { tierSchema } from './enums.js';

/**
 * Stripe subscription status, surfaced through `customer.subscription.*`
 * events. Maps onto `subscribers.status` per ARCH-001 §7 and PRD-003 DATA-01.
 *
 * Stripe statuses we map:
 *   - active, trialing  -> subscribers.status = 'active'
 *   - past_due, unpaid  -> subscribers.status = 'active' (we keep delivering;
 *                         Stripe will eventually move to canceled)
 *   - paused            -> subscribers.status = 'paused'
 *   - canceled,
 *     incomplete_expired -> subscribers.status = 'canceled'
 *   - incomplete        -> subscribers.status = 'active' (Stripe Checkout
 *                         flow has not yet completed the first invoice)
 */
export const stripeSubscriptionStatusSchema = z.enum([
  'active',
  'past_due',
  'unpaid',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'trialing',
  'paused',
]);
export type StripeSubscriptionStatus = z.infer<typeof stripeSubscriptionStatusSchema>;

/**
 * The minimum shape of a Stripe Checkout Session that the success-callback /
 * webhook flow consumes.
 */
export const stripeCheckoutSessionSchema = z
  .object({
    id: z.string().min(1),
    object: z.literal('checkout.session'),
    customer: z.string().min(1).nullable(),
    customer_email: z.string().email().nullable(),
    customer_details: z
      .object({
        email: z.string().email().nullable(),
      })
      .passthrough()
      .nullable(),
    subscription: z.string().min(1).nullable(),
    client_reference_id: z.string().nullable(),
    mode: z.string(),
    payment_status: z.string(),
    status: z.string().nullable(),
    metadata: z.record(z.string(), z.string()).default({}),
  })
  .passthrough();
export type StripeCheckoutSession = z.infer<typeof stripeCheckoutSessionSchema>;

/**
 * Minimum shape of a Stripe Subscription as seen on `customer.subscription.*`
 * events. Used by T-029 to derive tier + status.
 */
export const stripeSubscriptionSchema = z
  .object({
    id: z.string().min(1),
    object: z.literal('subscription'),
    customer: z.string().min(1),
    status: stripeSubscriptionStatusSchema,
    cancel_at_period_end: z.boolean().optional(),
    canceled_at: z.number().int().nullable().optional(),
    pause_collection: z
      .object({
        behavior: z.string(),
        resumes_at: z.number().int().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    items: z
      .object({
        data: z.array(
          z
            .object({
              price: z
                .object({
                  id: z.string().min(1),
                })
                .passthrough(),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
    metadata: z.record(z.string(), z.string()).default({}),
  })
  .passthrough();
export type StripeSubscription = z.infer<typeof stripeSubscriptionSchema>;

/**
 * Generic Stripe event envelope. Each handler discriminates on `type` and then
 * parses the object inside `data.object` with the per-event schema above.
 */
export const stripeEventEnvelopeSchema = z
  .object({
    id: z.string().min(1),
    object: z.literal('event'),
    type: z.string().min(1),
    livemode: z.boolean(),
    created: z.number().int().nonnegative(),
    api_version: z.string().nullable(),
    data: z
      .object({
        object: z.record(z.string(), z.unknown()),
      })
      .passthrough(),
  })
  .passthrough();
export type StripeEventEnvelope = z.infer<typeof stripeEventEnvelopeSchema>;

/**
 * The subset of event types T-029 handles.
 */
export const HANDLED_STRIPE_EVENT_TYPES = [
  'checkout.session.completed',
  'customer.subscription.created',
  'customer.subscription.updated',
  'customer.subscription.deleted',
  'customer.subscription.paused',
  'customer.subscription.resumed',
] as const;

export type HandledStripeEventType = (typeof HANDLED_STRIPE_EVENT_TYPES)[number];

export function isHandledStripeEventType(
  candidate: string,
): candidate is HandledStripeEventType {
  return (HANDLED_STRIPE_EVENT_TYPES as readonly string[]).includes(candidate);
}

/**
 * Capacity check + checkout request body coming from the portal SPA. Used by
 * the /api/checkout server handler (T-028).
 */
export const checkoutRequestSchema = z.object({
  tier: tierSchema,
  email: z.string().email().optional(),
});
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

/**
 * Response from /api/checkout. Either a Stripe Checkout URL, or a waitlist
 * redirect when the tier is at cap (PR-04 + TECH-13).
 */
export const checkoutResponseSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('checkout'),
    url: z.string().url(),
  }),
  z.object({
    outcome: z.literal('waitlist'),
    redirect: z.string().startsWith('/waitlist'),
    reason: z.literal('at_capacity'),
  }),
]);
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;

/**
 * Customer Portal session creation response (T-030).
 */
export const customerPortalResponseSchema = z.object({
  url: z.string().url(),
});
export type CustomerPortalResponse = z.infer<typeof customerPortalResponseSchema>;

/**
 * Reconciliation divergence row (T-031). Emitted as a notification payload
 * when Stripe and Supabase disagree.
 */
export const stripeReconciliationDivergenceSchema = z.object({
  subscriber_id: z.string().uuid().nullable(),
  stripe_customer_id: z.string().min(1),
  stripe_subscription_id: z.string().min(1).nullable(),
  stripe_status: stripeSubscriptionStatusSchema.nullable(),
  stripe_price_id: z.string().nullable(),
  supabase_status: z.string().nullable(),
  supabase_tier: tierSchema.nullable(),
  detected_at: isoTimestampSchema,
  note: z.string(),
});
export type StripeReconciliationDivergence = z.infer<
  typeof stripeReconciliationDivergenceSchema
>;
